/**
 * Resumes a pending login action once the reader is back, signed in, on the
 * page it came from.
 *
 * Every login ends there, whether it went back through history or reloaded
 * the app on the page's URL. The page waits until its own path is showing,
 * rather than for focus, since a page stays focused under the login and
 * profile setup routes, and until a first login has finished profile setup.
 */

import { useSiloLanguage } from 'component/translation';
import { useNeedsProfileSetup } from 'data/me';
import { usePathname } from 'expo-router';
import { useEffect, useEffectEvent } from 'react';
import { type Datastore, useDatastore, usePersonaKey } from 'util/datastore';
import { clearPendingLoginAction, getPendingLoginAction, type PendingLoginAction } from 'util/pending-login-action';

/** Opens the screen a pending login action was heading for. */
export function useResumeLoginDestination() {
    const datastore = useDatastore();
    useTakePendingLoginAction({
        matches: (pending) => !!pending.destination,
        run: (pending) => datastore.gotoLoginDestination(pending.destination!),
    });
}

/**
 * Runs `handler` with the pending action's params when it was stored under
 * `key` from this instance, as `needsLogin`'s `resume` option does.
 */
export function useResumeAfterLogin(key: string, handler: (params: Record<string, unknown>) => void) {
    useTakePendingLoginAction({
        matches: (pending) => pending.resume?.key === key,
        run: (pending) => handler(pending.resume!.params),
    });
}

function useTakePendingLoginAction({
    matches,
    run,
}: {
    matches: (pending: PendingLoginAction) => boolean;
    run: (pending: PendingLoginAction) => void;
}) {
    const datastore = useDatastore();
    const personaKey = usePersonaKey();
    const pathname = usePathname();
    const locale = useSiloLanguage();
    const needsSetup = useNeedsProfileSetup({ siloKey: datastore.getSiloKey(), profileKey: personaKey, locale });
    const take = useEffectEvent(() => {
        const pending = getPendingLoginAction();
        if (!pending || !isForThisPage({ pending, datastore, pathname }) || !matches(pending)) return;
        clearPendingLoginAction();
        run(pending);
    });

    useEffect(() => {
        if (personaKey && needsSetup === false) {
            take();
        }
    }, [personaKey, needsSetup, pathname]);
}

function isForThisPage({ pending, datastore, pathname }: { pending: PendingLoginAction; datastore: Datastore; pathname: string }) {
    if (!pending.returnUrl) return false;
    if (pending.structureKey !== datastore.getStructureKey() || pending.instanceKey !== datastore.getInstanceKey()) return false;
    return new URL(pending.returnUrl).pathname.endsWith(pathname);
}
